import { datadogLogs } from "@datadog/browser-logs";
import React, {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useImmerReducer } from "use-immer";

import { IdentityContextProvider, parseResource } from "@examspark/api";
import { parseAwsJson } from "@examspark/common";
import {
  AnyObject,
  AppConfig,
  ParsedApp,
  Resource,
  SystemEventType,
} from "@examspark/types";

import { useQueryGetApp } from "../api/operations/app";
import { useQueryGetAppLicenseConfig } from "../api/operations/app-license";
import { useSetIdentityContext } from "../hooks/useSetIdentityContext";
import { useSystemEventNotifications } from "../hooks/useSystemEventNotifications";
import { safeLocalStorage } from "../safeStorage";

import {
  AppContextActions,
  appContextReducer,
  AppState,
} from "./app-context/appContextReducer";

export const getObjectiveAuthorityIdsFromAppState = (appState: AppState) => {
  return appState.userPrefs?.objectiveAuthorityIds;
  // ?? appState.appConfig?.workspace?.objectiveAuthorityIds ?? ["tea"]
};

export const defaultState: AppState = { version: 0 };

export type AppContextValue = [AppState, (params: AppContextActions) => void];

const AppContext = createContext<AppContextValue>([
  defaultState,
  (action) => {
    console.error(
      `Attempted to dispatch to non-existent state. Are you missing AppContextProvider?`,
      action,
    );
  },
]);

export const useAppContext = <
  TAppConfig extends AnyObject = AppConfig,
  TAppLicenseConfig extends AnyObject = AnyObject,
  TAppUserConfig extends AnyObject = AnyObject,
>(): [
  state: AppState,
  dispatch: Dispatch<
    AppContextActions<TAppConfig, TAppLicenseConfig, TAppUserConfig>
  >,
] => {
  const [state, dispatch] = useContext(AppContext);

  return [
    state as AppState<TAppLicenseConfig, TAppUserConfig>,
    dispatch as (
      params: AppContextActions<TAppConfig, TAppLicenseConfig, TAppUserConfig>,
    ) => void,
  ] as const;
};

/**
 * Equivalent to `useAppContext`, except the dispatch method returns a promise
 * that resolves when the state version increments.
 * If always awaited, these promises can be used to serialize updates to the state.
 */
export const useAppContextSerializable = (): [
  state: AppState,
  dispatchAndWaitForNextVersion: (action: AppContextActions) => Promise<void>,
] => {
  //
  // Idea for future improvement:
  // * await the expected new version number instead of just a version change
  // * use the promises internally so we can serialize updates event if the caller
  //   doesn't await
  // * allow dispatching a batch of events and waiting till they all process
  //

  const [state, dispatch] = useAppContext();
  const emitter = useMemo(() => new EventTarget(), []);

  useEffect(() => {
    emitter.dispatchEvent(new Event("versionChanged"));
  }, [emitter, state.version]);

  const dispatchAndWait = useCallback(
    (action: AppContextActions) => {
      dispatch(action);

      return new Promise<void>((resolve) => {
        emitter.addEventListener(
          "versionChanged",
          () => {
            resolve();
          },
          { once: true },
        );
      });
    },
    [dispatch, emitter],
  );

  return [state, dispatchAndWait];
};

const initialState =
  safeLocalStorage.parseItem<AppState>("bonfire-state") ?? defaultState;

IdentityContextProvider.setContext({
  userId: initialState.user?.id ?? null,
  workspaceId: initialState.workspaceUser?.id ?? null,
  appId: initialState.app?.id ?? process.env.REACT_APP_EXAMSPARK_APP_ID ?? null,
});

const AppContextProvider: React.FC<
  React.PropsWithChildren<{
    codebaseName?: string;
  }>
> = ({ children, codebaseName }) => {
  const [state, dispatch] = useImmerReducer<AppState, AppContextActions>(
    appContextReducer,
    {
      ...initialState,
      version: 0,
    },
  );

  const { data: app } = useQueryGetApp(
    state.app?.id ?? process.env.REACT_APP_EXAMSPARK_APP_ID,
  );
  const { data: proxiedApp } = useQueryGetApp(app?.config?.proxyAppId);
  const configQuery = useQueryGetAppLicenseConfig(state);

  /**
   * System events: handle forcefully reloading browser window when certain
   * system-level events are fired. Useful for when the app is updated or when
   * needing downtime. */
  useSystemEventNotifications(
    codebaseName,
    (e) => {
      switch (e.type) {
        case SystemEventType.AppUpdate:
          if (parseAwsJson<{ forceReload?: boolean }>(e.data)?.forceReload) {
            window.location.reload();
          } else {
            dispatch({
              type: "APP_UPDATED",
            });
          }
          break;
      }
    },
    codebaseName != null &&
      // don't subscribe in student app for now, it's potentially costly
      codebaseName !== "student",
  );

  /** SET_APP upon successful fetch of the app */
  useEffect(() => {
    if (!app) return;

    const parsedApp = parseResource(app, (r) => ({
      config: parseAwsJson(r.config),
    })) as ParsedApp & {
      workspace: Resource["workspace"];
    };
    dispatch({
      type: "SET_APP",
      data: parsedApp,
    });
  }, [app, dispatch]);

  /** SET_PROXIED_APP upon successful fetch of the app */
  useEffect(() => {
    if (!proxiedApp) return;

    const parsedApp = parseResource(proxiedApp, (r) => ({
      config: parseAwsJson(r.config),
    })) as ParsedApp & {
      workspace: Resource["workspace"];
    };

    dispatch({
      type: "APP_CONTEXT_SET_PROXIED_APP",
      data: parsedApp,
    });
  }, [proxiedApp, dispatch]);

  /**
   * Save state to local storage whenever state changes.
   */
  useEffect(() => {
    safeLocalStorage.serializeItem("bonfire-state", {
      ...state,
      // do not persist system status value to app state
      systemStatus: undefined,
    });
  }, [state]);

  /**
   * Set DD context whenever state changes for richer logging.
   */
  useEffect(() => {
    datadogLogs.setGlobalContextProperty("session", {
      user: state.user,
      workspaceUser: state.workspaceUser,
      workspaceId: state.workspace?.id,
      app: state.app,
    });
  }, [state]);

  useEffect(() => {
    if (configQuery.data == null) return;

    dispatch({
      type: "SET_CONFIG",
      data: configQuery.data,
    });
  }, [configQuery.data, dispatch]);

  useSetIdentityContext(state);

  return (
    <AppContext.Provider value={[state, dispatch]}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
